import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { useTheme } from 'styled-components'
import { Typography } from '@chilecompra/react-kit/components'
import Instance from 'highcharts'
import TreemapAddOn from 'highcharts/modules/treemap'
import Highcharts from 'highcharts-react-official'
import { GlobalStyle, GridContainer, GridItem, Nav, NavIcon, NavItem } from './TreemapChart.styles'
import truncate from '../../modules/utils/limiter'

TreemapAddOn(Instance)

const TreemapChart = props => {
  const { Autocomplete, data, id, levels, rootIcon, rootName, labelFormatter, onClickMap, onClickTree, onReset } = props

  const theme = useTheme()
  const baseData = useMemo(
    () => [
      { color: theme.palette.charts.navyBlue, id: '1', name: '' },
      { color: theme.palette.charts.cobalt, id: '2', name: '' },
      { color: theme.palette.charts.fauxRoyal, id: '3', name: '' },
      { color: theme.palette.charts.midnight, id: '4', name: '' },
      { color: theme.palette.charts.navyBlue, id: '5', name: '' },
      { color: theme.palette.charts.cobalt, id: '6', name: '' },
      { color: theme.palette.charts.fauxRoyal, id: '7', name: '' },
      { color: theme.palette.charts.midnight, id: '8', name: '' },
      { color: theme.palette.charts.navyBlue, id: '9', name: '' },
      { color: theme.palette.charts.cobalt, id: '10', name: '' }
    ],
    [theme]
  )
  const baseValues = useMemo(() => [30, 8, 8, 5, 5, 5, 3, 3, 3, 3], [])
  const enhancedData = useMemo(() => {
    const dataLength = data.map((item, index) => ({
      ...item,
      color: baseData[index].color,
      value: baseValues[index]
    }))
    return dataLength
  }, [data, baseData, baseValues])

  const handleClickTree = (item, index) => () => onClickTree(item, index)

  const optionsTree = {
    accessibility: { enabled: false },
    chart: { type: 'treemap', height: '500px' },
    drilldown: { series: [] },
    plotOptions: { treemap: { cursor: 'pointer' } },
    series: [
      {
        alternateStartingDirection: false,
        background: theme.palette.brand.white,
        data: enhancedData,
        layoutAlgorithm: 'strip',
        nodeSizeBy: 'leaf',
        dataLabels: {
          formatter: labelFormatter,
          style: {
            fontSize: '12px',
            fontWeight: 'regular',
            textAlign: 'left',
            textOutline: 'none'
          },
          useHTML: true
        },
        levels: [
          {
            borderColor: theme.palette.brand.white,
            borderWidth: 4,
            level: 1
          }
        ],
        type: 'treemap',
        events: {
          click: event => {
            const { label, type, id } = event.point
            onClickMap(type, label, id)
          }
        }
      }
    ],
    title: { text: '' },
    tooltip: {
      enabled: true,
      useHTML: true,
      pointFormat: '{point.label}',
      style: {
        zIndex: 9999
      }
    }
  }

  return (
    <>
      <GlobalStyle />
      <GridContainer id={id}>
        <GridItem md={1.5} xs={12}>
          <Nav>
            <NavItem onClick={onReset}>
              <NavIcon>{rootIcon}</NavIcon>
              <Typography fontSize="12px" variant="body2">
                {rootName}
              </Typography>
            </NavItem>
            {levels.map((item, index) => (
              <NavItem key={index} onClick={handleClickTree(item, index)}>
                <NavIcon>{item.icon}</NavIcon>
                <Typography fontSize="12px" variant="body2">
                  {truncate(item.name, 40, ' ...')}
                </Typography>
              </NavItem>
            ))}
          </Nav>
        </GridItem>
        <GridItem md={10.5} xs={12}>
          <GridContainer>
            {Autocomplete}
            <GridItem xs={12}>
              <Highcharts highcharts={Instance} options={optionsTree} />
            </GridItem>
          </GridContainer>
        </GridItem>
      </GridContainer>
    </>
  )
}

TreemapChart.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
      parent: PropTypes.string,
      value: PropTypes.number,
      label: PropTypes.string,
      index: PropTypes.number,
      color: PropTypes.string
    })
  ).isRequired,
  id: PropTypes.string.isRequired,
  levels: PropTypes.arrayOf(
    PropTypes.shape({
      icon: PropTypes.node,
      name: PropTypes.string
    })
  ).isRequired,
  rootIcon: PropTypes.node.isRequired,
  rootName: PropTypes.string.isRequired,
  Autocomplete: PropTypes.node,
  labelFormatter: PropTypes.func.isRequired,
  onClickMap: PropTypes.func.isRequired,
  onClickTree: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired
}

export default TreemapChart
